import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Separator from "../assets/Separator.svg";
import NewsLetterSub from "../assets/NewsLetterSub.svg";
import { Text } from "./NewsLetterComp";
import BlogCard from "./BlogCard";
import { blogPosts } from "../data/blogPosts";

const ACCENT = "#1ACBAF";

const NEWSLETTER_URL =
  "https://57927b91.sibforms.com/serve/MUIFACjnqNUefDpSiojhOuNkkIEbfsBJK-hG9jGMpZHvtBYbVY5b8Y6qIfrLFrvm9mD6dnZ_m4zGq-u9bjCwMvuQHfxqEGm3F0qLLsMeL5aT2uu5BFlIkSRmG5zwGE6Z-zb_XN9iS-btTBYdT8agLzZXOYF8r4k0_-fPSZNee3flMfpiPB81jZvyv0fkTGsJgBzlgDPt7FCd2k9b";

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const Heading = ({ id, text }) => (
  <Stack
    id={id}
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mt: { xs: 4, sm: 5 }, mb: 1, scrollMarginTop: "24px" }}
  >
    <Box sx={{ width: "20px", height: "3px", borderRadius: "2px", background: ACCENT, flexShrink: 0 }} />
    <Typography
      sx={{
        fontFamily: "Righteous",
        fontWeight: 400,
        fontSize: { xs: "19px", sm: "22px" },
        color: "#FFFFFF",
        lineHeight: 1.4,
      }}
    >
      {text}
    </Typography>
  </Stack>
);

const Paragraph = ({ text, lead }) => (
  <Text
    sx={{
      fontWeight: lead ? 400 : 400,
      fontSize: lead ? { xs: "17px", sm: "19px" } : { xs: "15.5px", sm: "17px" },
      lineHeight: lead ? 1.7 : 1.95,
      color: lead ? "#E9EAF0" : "#D2D5DF",
      letterSpacing: "0.1px",
    }}
  >
    {text}
  </Text>
);

const Quote = ({ text, caption }) => (
  <Box
    sx={{
      my: 1,
      py: 2,
      px: { xs: 2.5, sm: 3 },
      borderLeft: `3px solid ${ACCENT}`,
      background: "#FFFFFF08",
      borderRadius: "0 10px 10px 0",
    }}
  >
    <Typography
      sx={{
        fontFamily: "Righteous",
        fontWeight: 400,
        fontStyle: "italic",
        fontSize: { xs: "17px", sm: "20px" },
        lineHeight: 1.6,
        color: "#FFFFFF",
        mb: caption ? 1 : 0,
      }}
    >
      “{text}”
    </Typography>
    {caption && (
      <Text
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          letterSpacing: "1px",
          textTransform: "uppercase",
          color: "#8B8FA3",
        }}
      >
        {caption}
      </Text>
    )}
  </Box>
);

const LabeledList = ({ items }) => (
  <Stack spacing={2}>
    {items.map((item, index) => (
      <Text
        key={index}
        sx={{ fontWeight: 400, fontSize: { xs: "15.5px", sm: "17px" }, lineHeight: 1.95, color: "#D2D5DF" }}
      >
        <Box component="span" sx={{ color: ACCENT, fontWeight: 700 }}>
          {item.label}
        </Box>{" "}
        {item.text}
      </Text>
    ))}
  </Stack>
);

const NumberedList = ({ items }) => (
  <Stack spacing={3}>
    {items.map((item, index) => (
      <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            flexShrink: 0,
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(142.21deg, rgba(27, 51, 81, 0.8) 19.63%, rgba(67, 144, 238, 0.8) 83.01%)",
            border: "0.6px solid #FFFFFF40",
          }}
        >
          <Typography sx={{ fontFamily: "Righteous", fontSize: "13px", color: "#FFFFFF" }}>
            {index + 1}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: "Raleway",
              fontWeight: 700,
              fontSize: { xs: "15px", sm: "16.5px" },
              color: "#FFFFFF",
              mb: 0.75,
            }}
          >
            {item.title}
          </Typography>
          <Text sx={{ fontWeight: 400, fontSize: { xs: "14px", sm: "15.5px" }, lineHeight: 1.9, color: "#B7BAC7" }}>
            {item.text}
          </Text>
        </Box>
      </Stack>
    ))}
  </Stack>
);

const renderBlock = (block, index, isFirstParagraph) => {
  switch (block.type) {
    case "heading":
      return <Heading key={index} id={slugify(block.text)} text={block.text} />;
    case "quote":
      return <Quote key={index} text={block.text} caption={block.caption} />;
    case "labeledList":
      return <LabeledList key={index} items={block.items} />;
    case "numberedList":
      return <NumberedList key={index} items={block.items} />;
    case "paragraph":
    default:
      return <Paragraph key={index} text={block.text} lead={isFirstParagraph} />;
  }
};

const TableOfContents = ({ headings, activeId }) => {
  if (headings.length === 0) return null;

  const handleClick = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box
      component="nav"
      sx={{
        display: { xs: "none", md: "block" },
        width: "220px",
        flexShrink: 0,
        position: "sticky",
        top: "40px",
        alignSelf: "flex-start",
        maxHeight: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Text
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          color: "#8B8FA3",
          mb: 2,
          display: "block",
        }}
      >
        On this page
      </Text>
      <Stack spacing={1.4} sx={{ borderLeft: "1px solid #FFFFFF1F", pl: 2 }}>
        {headings.map((h) => (
          <Box
            key={h.id}
            component="a"
            href={`#${h.id}`}
            onClick={(e) => handleClick(e, h.id)}
            sx={{
              textDecoration: "none",
              fontFamily: "Raleway",
              fontSize: "13px",
              lineHeight: 1.4,
              color: activeId === h.id ? ACCENT : "#9FA2AC",
              fontWeight: activeId === h.id ? 700 : 400,
              transition: "color 0.15s ease",
              "&:hover": { color: ACCENT },
            }}
          >
            {h.text}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const newsletterPerks = [
  "New essays as soon as they publish",
  "Product and research updates",
  "No spam, unsubscribe anytime",
];

const NewsletterCard = () => (
  <Box
    sx={{
      width: "100%",
      boxSizing: "border-box",
      borderRadius: "16px",
      position: "relative",
      overflow: "hidden",
      p: { xs: 2.5, sm: 3 },
      background:
        "linear-gradient(142.21deg, rgba(27, 51, 81, 0.3) 19.63%, rgba(67, 144, 238, 0.3) 83.01%)",
      backgroundBlendMode: "overlay",
      border: "0.6px solid",
      borderImageSource: "linear-gradient(178.6deg, #FFFFFF 13.55%, rgba(0, 0, 0, 0.15) 83.77%)",
      boxShadow: "0px 23.9px 23.9px 0px #00000040",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "-40px",
        right: "-40px",
        width: { xs: "110px", sm: "160px" },
        height: { xs: "110px", sm: "160px" },
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(67, 144, 238, 0.35) 0%, rgba(67, 144, 238, 0) 70%)",
        pointerEvents: "none",
      }}
    />

    <Box
      sx={{
        width: { xs: "38px", sm: "44px" },
        height: { xs: "38px", sm: "44px" },
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(142.21deg, rgba(27, 51, 81, 0.9) 19.63%, rgba(67, 144, 238, 0.9) 83.01%)",
        border: "0.6px solid #FFFFFF40",
        mb: 2,
      }}
    >
      <MarkEmailReadRoundedIcon sx={{ color: "#FFFFFF", fontSize: { xs: "19px", sm: "22px" } }} />
    </Box>

    <Typography
      sx={{
        fontFamily: "Righteous",
        fontWeight: 400,
        fontSize: { xs: "16px", sm: "18px" },
        color: "#FFFFFF",
        mb: 1,
        lineHeight: 1.4,
      }}
    >
      Get the next essay first
    </Typography>
    <Text sx={{ fontSize: { xs: "12.5px", sm: "13px" }, lineHeight: 1.7, color: "#B7BAC7", mb: 2, display: "block" }}>
      Join our newsletter for new essays, product updates, and notes from the
      Veracious team — straight to your inbox.
    </Text>

    <Stack spacing={1.1} sx={{ mb: 2.5 }}>
      {newsletterPerks.map((perk) => (
        <Stack key={perk} direction="row" spacing={1} alignItems="flex-start">
          <CheckCircleRoundedIcon sx={{ fontSize: "15px", color: ACCENT, mt: "2px", flexShrink: 0 }} />
          <Text sx={{ fontSize: "12.5px", lineHeight: 1.5, color: "#C2C2C2" }}>{perk}</Text>
        </Stack>
      ))}
    </Stack>

    <MuiLink href={NEWSLETTER_URL} target="_blank" underline="none" sx={{ display: "block" }}>
      <Button
        variant="contained"
        fullWidth
        sx={{
          background: "#21426B !important",
          textTransform: "none",
          py: "10px",
          borderRadius: "8px",
          boxShadow: "0px 12px 24px 0px #00000040",
          transition: "transform 0.15s ease, background 0.15s ease",
          "&:hover": { background: "#2e507f !important", transform: "translateY(-2px)" },
        }}
      >
        <img style={{ marginRight: "10px", flexShrink: 0 }} src={NewsLetterSub} alt="" />
        <Text
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            whiteSpace: "nowrap",
            backgroundImage: "linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.5) 100%)",
            backgroundClip: "text",
          }}
        >
          Subscribe for free
        </Text>
      </Button>
    </MuiLink>

    <Text sx={{ fontSize: "11px", color: "#8B8FA3", textAlign: "center", mt: 1.5, display: "block" }}>
      Join 100,000+ readers
    </Text>
  </Box>
);

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((item) => item.slug === slug);
  const otherPosts = blogPosts.filter((item) => item.slug !== slug).slice(0, 3);

  const headings = useMemo(
    () =>
      (post?.content || [])
        .filter((block) => block.type === "heading")
        .map((block) => ({ id: slugify(block.text), text: block.text })),
    [post]
  );

  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    if (headings.length === 0) return;
    const elements = headings.map((h) => document.getElementById(h.id)).filter(Boolean);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headings]);

  if (!post) {
    return (
      <Stack alignItems="center" textAlign="center" sx={{ py: 10, px: 3 }} spacing={2}>
        <Typography sx={{ fontFamily: "Righteous", color: "#FFFFFF", fontSize: "20px" }}>
          We couldn't find that post
        </Typography>
        <Link to="/blog" style={{ textDecoration: "none" }}>
          <Text sx={{ color: ACCENT, fontSize: "14px" }}>← Back to Blog</Text>
        </Link>
      </Stack>
    );
  }

  let paragraphSeen = false;

  return (
    <Box sx={{ px: 3, py: { xs: 4, lg: 5 }, position: "relative", zIndex: 1 }}>
      <Box sx={{ maxWidth: "1160px", mx: "auto" }}>
        <Link to="/blog" style={{ textDecoration: "none" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: { xs: 4, sm: 5 }, color: "#9FA2AC", "&:hover": { color: ACCENT } }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: "18px" }} />
            <Text sx={{ fontSize: "13px", color: "inherit" }}>Back to Blog</Text>
          </Stack>
        </Link>

        <Stack alignItems="center" textAlign="center" spacing={2}>
          {(post.kicker || post.category) && (
            <Stack direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" spacing={1}>
              {post.kicker && (
                <Text
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    color: "#8B8FA3",
                  }}
                >
                  {post.kicker}
                </Text>
              )}
              {post.kicker && post.category && (
                <Box sx={{ width: "4px", height: "4px", borderRadius: "50%", background: "#8B8FA3" }} />
              )}
              {post.category && (
                <Text
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    color: ACCENT,
                  }}
                >
                  {post.category}
                </Text>
              )}
            </Stack>
          )}

          <Typography
            sx={{
              fontFamily: "Righteous",
              fontWeight: 400,
              fontSize: { xs: "28px", sm: "40px" },
              backgroundImage:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.55) 100%)",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: 1.25,
            }}
          >
            {post.title}
          </Typography>

          {post.dek && (
            <Text
              sx={{
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px" },
                lineHeight: 1.6,
                color: "#B7BAC7",
                maxWidth: "560px",
              }}
            >
              {post.dek}
            </Text>
          )}

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            rowGap={0.5}
            sx={{ pt: 0.5 }}
          >
            {post.author && <Text sx={{ fontSize: "13px", color: "#8B8FA3" }}>{post.author}</Text>}
            {post.author && (post.date || post.readTime) && (
              <Box sx={{ width: "4px", height: "4px", borderRadius: "50%", background: "#8B8FA3" }} />
            )}
            {post.date && <Text sx={{ fontSize: "13px", color: "#8B8FA3" }}>{post.date}</Text>}
            {post.date && post.readTime && (
              <Box sx={{ width: "4px", height: "4px", borderRadius: "50%", background: "#8B8FA3" }} />
            )}
            {post.readTime && <Text sx={{ fontSize: "13px", color: "#8B8FA3" }}>{post.readTime}</Text>}
          </Stack>
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "center", my: { xs: 4, sm: 5 } }}>
          <img width="100%" src={Separator} alt="" />
        </Box>

        {post.image && (
          <Box sx={{ borderRadius: "16px", overflow: "hidden", mb: 4, width: "100%" }}>
            <img
              src={post.image}
              alt={post.title}
              style={{ width: "100%", maxHeight: "480px", objectFit: "cover", display: "block" }}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: { md: 5 }, alignItems: "flex-start" }}>
          <TableOfContents headings={headings} activeId={activeId} />

          <Stack spacing={2.75} sx={{ flex: 1, maxWidth: "640px", mx: { xs: "auto", md: 0 } }}>
            {(post.content || []).map((block, index) => {
              const isFirstParagraph = block.type === "paragraph" && !paragraphSeen;
              if (isFirstParagraph) paragraphSeen = true;
              return renderBlock(block, index, isFirstParagraph);
            })}
          </Stack>

          <Box
            sx={{
              display: { xs: "none", md: "block" },
              width: "260px",
              flexShrink: 0,
              position: "sticky",
              top: "40px",
              alignSelf: "flex-start",
            }}
          >
            <NewsletterCard />
          </Box>
        </Box>

        <Box sx={{ display: { xs: "block", md: "none" }, maxWidth: "480px", mx: "auto", mt: 4 }}>
          <NewsletterCard />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 5, sm: 6 }, mb: 2 }}>
          <img width="100%" src={Separator} alt="" />
        </Box>

        {otherPosts.length > 0 && (
          <Box sx={{ py: { xs: 2, sm: 3 } }}>
            <Typography
              sx={{
                fontFamily: "Righteous",
                fontWeight: 400,
                fontSize: { xs: "20px", sm: "24px" },
                color: "#FFFFFF",
                textAlign: "center",
                mb: 3,
              }}
            >
              More from the blog
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 4,
              }}
            >
              {otherPosts.map((item) => (
                <BlogCard key={item.id} {...item} />
              ))}
            </Box>
          </Box>
        )}

        <Stack alignItems="center" sx={{ pb: 4, pt: otherPosts.length > 0 ? 2 : 0 }}>
          <Link to="/blog" style={{ textDecoration: "none" }}>
            <Text sx={{ fontSize: "13px", color: ACCENT }}>← Back to all posts</Text>
          </Link>
        </Stack>
      </Box>
    </Box>
  );
};

export default BlogPost;
